// src/pages/CausesEvents.jsx
import React, { useState } from 'react';
import { 
  Box, Button, Typography, TextField, 
  Grid, Card, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Paper, 
  TablePagination, Avatar, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, LinearProgress,
  FormControlLabel, Switch, FormGroup
} from '@mui/material';
import { 
  Download as DownloadIcon, 
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  BarChart as BarChartIcon,
  CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

const causeColumns = [
  { id: 'id', label: 'ID', minWidth: 70 },
  { id: 'title', label: 'Title', minWidth: 200 },
  { id: 'type', label: 'Type', minWidth: 100 },
  { id: 'dates', label: 'Dates', minWidth: 150 },
  { id: 'progress', label: 'Progress', minWidth: 150 },
  { id: 'status', label: 'Status', minWidth: 100 },
  { id: 'actions', label: 'Actions', minWidth: 150, align: 'center' },
];

const statusOptions = ['Planning', 'Active', 'Completed', 'Archived'];
const typeOptions = ['Cause', 'Event', 'Campaign'];

const mockCauses = Array.from({ length: 20 }, (_, i) => {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - Math.floor(Math.random() * 30));
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + Math.floor(Math.random() * 60) + 7);
  
  const targetAmount = Math.floor(Math.random() * 500000) + 100000;
  const raisedAmount = Math.floor(targetAmount * Math.random() * 0.8);
  
  return {
    id: i + 1,
    title: `Cause ${i + 1}`,
    description: `Description for cause ${i + 1}`,
    type: typeOptions[Math.floor(Math.random() * typeOptions.length)],
    category: ['Education', 'Environment', 'Health', 'Poverty'][Math.floor(Math.random() * 4)],
    startDate,
    endDate,
    targetAmount,
    raisedAmount,
    status: statusOptions[Math.floor(Math.random() * statusOptions.length)],
    volunteers: Math.floor(Math.random() * 20),
    image: `/causes/${Math.floor(Math.random() * 5) + 1}.jpg`,
  };
});

const CausesEvents = () => {
  const [causes, setCauses] = useState(mockCauses);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    category: '',
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [currentCause, setCurrentCause] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [causeToDelete, setCauseToDelete] = useState(null);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSearch = (e) => setSearchText(e.target.value);
  const handleResetFilters = () => {
    setFilters({
      status: '',
      type: '',
      category: '',
    });
    setSearchText('');
  };

  const handleExport = () => console.log('Export causes/events');
  
  const handleViewCause = (cause) => {
    setCurrentCause(cause);
    setOpenDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentCause(null);
  };
  
  const handleOpenDeleteDialog = (cause) => {
    setCauseToDelete(cause);
    setOpenDeleteDialog(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setCauseToDelete(null);
  };
  
  const handleDeleteCause = () => {
    if (causeToDelete) {
      setCauses(prev => prev.filter(c => c.id !== causeToDelete.id));
      handleCloseDeleteDialog();
    }
  };
  
  const handleAddCause = () => {
    setCurrentCause({
      id: causes.length + 1,
      title: '',
      description: '',
      type: 'Cause',
      category: '',
      startDate: new Date(),
      endDate: new Date(new Date().setDate(new Date().getDate() + 30)),
      targetAmount: 0,
      raisedAmount: 0,
      status: 'Planning',
      volunteers: 0,
      image: ''
    });
    setOpenDialog(true);
  };
  
  const handleSaveCause = () => {
    if (currentCause) {
      if (currentCause.id > causes.length) {
        // New cause
        setCauses(prev => [...prev, currentCause]);
      } else {
        // Update existing
        setCauses(prev => prev.map(c => c.id === currentCause.id ? currentCause : c));
      }
      handleCloseDialog();
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentCause(prev => ({ ...prev, [name]: value }));
  };

  const filteredCauses = causes.filter(cause => {
    return (
      (filters.status ? cause.status === filters.status : true) &&
      (filters.type ? cause.type === filters.type : true) &&
      (filters.category ? cause.category === filters.category : true) &&
      (searchText 
        ? cause.title.toLowerCase().includes(searchText.toLowerCase()) || 
          cause.description.toLowerCase().includes(searchText.toLowerCase()) 
        : true)
    );
  });

  const statusColor = {
    Planning: 'default',
    Active: 'success',
    Completed: 'primary',
    Archived: 'secondary'
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>Causes & Events</Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={handleAddCause}
        >
          Add Cause/Event
        </Button>
        <Box display="flex" gap={1}>
          <Button 
            variant="outlined" 
            startIcon={<FilterListIcon />}
            onClick={handleResetFilters}
          >
            Reset Filters
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Export
          </Button>
        </Box>
      </Box>
      
      <Card sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search causes/events..."
              value={searchText}
              onChange={handleSearch}
              InputProps={{
                startAdornment: <SearchIcon color="action" />,
              }}
            />
          </Grid>
          
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                {statusOptions.map(status => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                name="type"
                value={filters.type}
                onChange={handleFilterChange}
                label="Type"
              >
                <MenuItem value="">All</MenuItem>
                {typeOptions.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                label="Category"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Education">Education</MenuItem>
                <MenuItem value="Environment">Environment</MenuItem>
                <MenuItem value="Health">Health</MenuItem>
                <MenuItem value="Poverty">Poverty</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>
      
      <Card>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {causeColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    style={{ minWidth: column.minWidth, fontWeight: 600 }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCauses
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((cause) => {
                  const progress = (cause.raisedAmount / cause.targetAmount) * 100;
                  return (
                    <TableRow hover role="checkbox" tabIndex={-1} key={cause.id}>
                      <TableCell>{cause.id}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar src={cause.image} sx={{ width: 40, height: 40, mr: 2 }}>
                            <ImageIcon />
                          </Avatar>
                          {cause.title}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={cause.type} color="primary" size="small" />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {cause.startDate.toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2">
                            to {cause.endDate.toLocaleDateString()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" gutterBottom>
                            ₹{cause.raisedAmount.toLocaleString()} of ₹{cause.targetAmount.toLocaleString()}
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={progress > 100 ? 100 : progress} 
                            sx={{ height: 8, borderRadius: 4 }}
                            color={progress > 75 ? 'success' : progress > 50 ? 'primary' : 'warning'}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {Math.round(progress)}% funded
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={cause.status} 
                          color={statusColor[cause.status] || 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <IconButton 
                            size="small"
                            onClick={() => handleViewCause(cause)}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small"
                            onClick={() => handleOpenDeleteDialog(cause)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredCauses.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      {/* Cause/Event Detail Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle>
          {currentCause?.id > causes.length ? 'Add New Cause/Event' : 'Edit Cause/Event'}
        </DialogTitle>
        <DialogContent dividers>
          {currentCause && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Title"
                  name="title"
                  value={currentCause.title}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={currentCause.description}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                  multiline
                  rows={4}
                />
                <FormControl fullWidth margin="normal">
                  <InputLabel>Type</InputLabel>
                  <Select
                    name="type"
                    value={currentCause.type}
                    onChange={handleInputChange}
                    label="Type"
                  >
                    {typeOptions.map(type => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={currentCause.category}
                    onChange={handleInputChange}
                    label="Category"
                  >
                    <MenuItem value="Education">Education</MenuItem>
                    <MenuItem value="Environment">Environment</MenuItem>
                    <MenuItem value="Health">Health</MenuItem>
                    <MenuItem value="Poverty">Poverty</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Start Date"
                    value={currentCause.startDate}
                    onChange={(newValue) => setCurrentCause({...currentCause, startDate: newValue})}
                    renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                  />
                  <DatePicker
                    label="End Date"
                    value={currentCause.endDate}
                    onChange={(newValue) => setCurrentCause({...currentCause, endDate: newValue})}
                    renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                  />
                </LocalizationProvider>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Target Amount (₹)"
                      name="targetAmount"
                      value={currentCause.targetAmount}
                      onChange={handleInputChange}
                      margin="normal"
                      variant="outlined"
                      type="number"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Raised Amount (₹)"
                      name="raisedAmount"
                      value={currentCause.raisedAmount}
                      onChange={handleInputChange}
                      margin="normal"
                      variant="outlined"
                      type="number"
                    />
                  </Grid>
                </Grid>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={currentCause.status}
                    onChange={handleInputChange}
                    label="Status"
                  >
                    {statusOptions.map(status => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Featured Image URL"
                  name="image"
                  value={currentCause.image}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    endAdornment: <ImageIcon color="action" />,
                  }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSaveCause} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{causeToDelete?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteCause} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CausesEvents;